import {
    Box,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { type ChangeEvent, useCallback, useMemo } from 'react';
import type { GetProductsResItem2ProductsItemDto } from '@travelity/api/generated';
import { calcDiscount } from '@/views/add-package/components/PricingStep/heleprs';
import { getMinPriceFromDualCurrency } from '@/utils/getMinPriceFromDualCurrency';
import { getMinPriceWithCurrency } from './helpers';

interface PackageFinancialsFieldsProps {
    products: GetProductsResItem2ProductsItemDto[];
    isEditing?: boolean;
}
export const PackageFinancialsFields = ({
    products,
    isEditing,
}: PackageFinancialsFieldsProps) => {
    const { control } = useFormContext();
    const amount = useWatch({ name: 'amount', control }) ?? 0;
    const discountType = useWatch({ name: 'type', control }) ?? 'absolute';

    const totalMinPrice = useMemo(
        () => getMinPriceWithCurrency(products),
        [products]
    );

    const discountAmount = useMemo(
        () =>
            calcDiscount(
                { amount, type: discountType },
                totalMinPrice.minPrice
            ),
        [amount, discountType, totalMinPrice.minPrice]
    );
    const finalTotal = Math.max(0, totalMinPrice.minPrice - discountAmount);

    const parseAmount = useCallback(
        (rawValue: string) => {
            const digitsOnly = rawValue.replace(/\D/g, '');
            const nextValue = digitsOnly === '' ? 0 : Number(digitsOnly);

            if (discountType === 'relative') {
                return Math.min(100, Math.max(0, nextValue));
            }

            return nextValue;
        },
        [discountType]
    );

    const createAmountChangeHandler = useCallback(
        (onChange: (value: number) => void) =>
            (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                onChange(parseAmount(e.target.value));
            },
        [parseAmount]
    );

    return (
        <>
            {products.map(p => (
                <div key={p.day_index}>
                    Day {p.offset_days + 1}
                    {p.items.map(({ id, ref, pricing }) => {
                        const prices = pricing?.items.map(priceItem =>
                            getMinPriceFromDualCurrency(
                                priceItem.price.original
                            )
                        );
                        const minPrice = Math.min(...(prices ?? [0]));
                        const currency = pricing?.items[0].currency.abbr;

                        return (
                            <Box
                                key={id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body1">
                                    {ref.name}
                                </Typography>
                                <Typography variant="body1">
                                    {minPrice}
                                    <small>{currency}</small>
                                </Typography>
                            </Box>
                        );
                    })}
                </div>
            ))}
            <Typography variant="body1">
                Subtotal: {totalMinPrice.minPrice}
                <small>{totalMinPrice.currency}</small>
            </Typography>
            <Stack direction="row" alignItems="center" gap={2} width="100%">
                <Controller
                    control={control}
                    name="amount"
                    render={({ field }) => {
                        const handleAmountChange = createAmountChangeHandler(
                            field.onChange
                        );

                        return (
                            <TextField
                                fullWidth
                                disabled={!isEditing}
                                name={field.name}
                                inputRef={field.ref}
                                inputProps={{
                                    min:
                                        discountType === 'relative'
                                            ? 0
                                            : undefined,
                                    max:
                                        discountType === 'relative'
                                            ? 100
                                            : undefined,
                                    inputMode: 'numeric',
                                }}
                                onBlur={field.onBlur}
                                onChange={handleAmountChange}
                                value={field.value ?? 0}
                            />
                        );
                    }}
                />
                <Controller
                    control={control}
                    name="type"
                    disabled={!isEditing}
                    render={({ field }) => (
                        <Select
                            {...field}
                            value={field.value}
                            sx={{
                                minWidth: 124,
                                '& .MuiSelect-select': {
                                    fontWeight: 700,
                                },
                            }}
                        >
                            <MenuItem value="relative">%</MenuItem>
                            <MenuItem value="absolute">Absolute</MenuItem>
                        </Select>
                    )}
                />
            </Stack>
            <Typography variant="body1">
                Total:{finalTotal}
                <small>{totalMinPrice.currency}</small>
            </Typography>
        </>
    );
};
